import {
  call,
  put,
  takeLatest,
  fork,
  select,
  all,
  take
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { isEmpty, get } from 'lodash';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import {
  permissions as perms,
  util as permissionUtil
} from 'functional-permissions-lib';

import {
  fetchEnabledApps,
  refreshApiToken,
  userIsAuthenticated,
  LOGOUT_SUCCESS,
  logout,
  selectUser,
  FETCH_USER_SUCCESS,
  LOGIN_SUCCESS,
  REFRESH_TOKEN_SUCCESS,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FAILURE,
  UPDATE_BASIC_INFO_USER_FAILURE
} from 'modules/user';
import { getConfig } from 'modules/config';
import { showNotification } from 'modules/snackbar';

import {
  CREATE_SUCCESS as CREATE_APPLICATION_SUCCESS,
  DELETE_SUCCESS as DELETE_APPLICATION_SUCCESS
} from 'modules/application';

import importPendo from 'resources/js/vendor/pendo';

export function* refreshToken() {
  const twoHours = 2 * 60 * 60 * 1000;

  try {
    const isAuthed = yield select(userIsAuthenticated);

    if (!isAuthed) {
      return;
    }

    yield put(refreshApiToken());
  } catch (error) {
    // cancellation error
  } finally {
    yield call(delay, twoHours);
  }
}

// Refresh api token immediately, and at an interval while app is active
function* watchPollRefreshApiToken() {
  yield call(delay, 20000); // wait a while for initial app load

  while (true) {
    yield call(refreshToken);
  }
}

export function* logoutIfUnauthorized(action) {
  if (action.error && action.payload && action.payload.status === 401) {
    if (yield select(userIsAuthenticated)) {
      yield put(logout());
    }
  }
}

function* watchedUnAuthorizedActions() {
  yield takeLatest('*', logoutIfUnauthorized);
}

// LOGOUT_SUCCESS action means the user's cookie has been invalidated. now
// refresh the page.
export function* handleLogout() {
  // TODO: uncomment this and get the test to pass
  // window.sessionStorage.clear();
  yield call([window.location, window.location.reload]);
}

function* watchLogout() {
  yield takeLatest(LOGOUT_SUCCESS, handleLogout);
}

const noAccessRedirect = '/no-access';
function* handleVerifyDeveloperAccess() {
  const user = yield select(selectUser);

  if (isEmpty(user)) {
    // not logged in or not yet fetched
    return;
  }

  if (
    !permissionUtil.hasAccessTo(perms.developer.access, user.permissionMasks)
  ) {
    yield put(replace(noAccessRedirect));
  }
}

function* watchDeveloperAccess() {
  yield takeLatest(
    [
      FETCH_USER_SUCCESS,
      LOGIN_SUCCESS,
      REFRESH_TOKEN_SUCCESS,
      action =>
        action.type === LOCATION_CHANGE &&
        action.payload.pathname !== noAccessRedirect
    ],
    action => handleVerifyDeveloperAccess(action.payload)
  );
}

// ensure appswitcher is populated with newly-created apps, and that newly-deleted
// apps don't show up anymore.
function* watchRefreshEnabledApplications() {
  yield takeLatest(
    [CREATE_APPLICATION_SUCCESS, DELETE_APPLICATION_SUCCESS],
    function*() {
      yield put(fetchEnabledApps());
    }
  );
}

function* initializePendoAfterUserLogin() {
  yield take([FETCH_USER_SUCCESS, LOGIN_SUCCESS]);
  const user = yield select(selectUser);

  const config = yield select(getConfig);
  if (!config.pendoKey) {
    return;
  }

  importPendo(config.pendoKey);

  window.pendo.initialize({
    visitor: {
      id: user.userId,
      email: user.userName
    },
    account: {
      id: get(user, 'groups[0].groupId'),
      groupname: get(user, 'groups[0].groupName'),
      organizationId: user.organization.organizationId,
      organizationName: user.organization.organizationName
    }
  });
}

const overviewRedirect = '/overview';
function* watchUpdateProfileSuccess() {
  // redirect to overview page after update profile success
  yield takeLatest(UPDATE_PROFILE_SUCCESS, function*() {
    yield put(replace(overviewRedirect));
  });
}

function* watchUpdateProfileFailure() {
  const message = 'Something went wrong; please try again later.';
  yield takeLatest(
    [UPDATE_PROFILE_FAILURE, UPDATE_BASIC_INFO_USER_FAILURE],
    function*() {
      yield put(showNotification(message));
    }
  );
}

export default function* root() {
  yield all([
    fork(watchPollRefreshApiToken),
    fork(watchLogout),
    fork(watchedUnAuthorizedActions),
    fork(watchDeveloperAccess),
    fork(watchRefreshEnabledApplications),
    fork(initializePendoAfterUserLogin),
    fork(watchUpdateProfileSuccess),
    fork(watchUpdateProfileFailure)
  ]);
}



// WEBPACK FOOTER //
// ./src/client/modules/user/sagas.js